import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { UseMultiAutocompleteParams } from "./MultiAutocomplete.types";

export const useMultiAutocomplete = <V, R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseMultiAutocompleteParams<V, R>) => {
  const autocompleteRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<Array<V>>(value ?? defaultValue ?? []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: autocompleteRef.current,
        setValue: (v: Array<V>) => {
          setState(v);
        },
        reset: () => {
          setState([]);
        },
      };
    }) as unknown as () => R,
    []
  );

  const onChangeHandler = (newValue: Array<V> | null) => {
    if (typeof value === "undefined") {
      setState(newValue ?? []);
    }
    onChange?.(newValue ?? []);
  };

  const onClearStateHandler = () => {
    if (typeof value === "undefined") {
      setState([]);
    }
    onChange?.([]);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state) as Array<V>,
    [value, state]
  );

  return {
    autocompleteRef,
    valueState,
    onChangeHandler,
    onClearStateHandler,
  };
};
